import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { HeartHandshake, Plus, MessageSquareHeart, Gift, Globe, Lock, Check, X, Share2, ChevronDown, Mail, Sparkles, ArrowRight, Target, UserPlus, Clock } from "lucide-react";
import { toast } from "sonner";
import GratitudeCard from "../components/GratitudeCard";
import AddGratitudeDialog from "../components/AddGratitudeDialog";
import EditGratitudeDialog from "../components/EditGratitudeDialog";
import MatchedPairs from "../components/MatchedPairs";
import GratitudeTimeline from "../components/GratitudeTimeline";
import SeedGratitudeDialog from "../components/SeedGratitudeDialog";
import SEO from "../components/SEO";

const API = "/api";

const FILTERS = [
  { key: "both", label_key: "gratitude.tabBoth" },
  { key: "mine", label_key: "gratitude.tabSelf" },
  { key: "received", label_key: "gratitude.tabReceived" },
];

// --- Pending asks banner ---------------------------------------------------

const PendingAsksBanner = ({ asks, onRespond, onDecline }) => {
  if (!asks || asks.length === 0) return null;
  return (
    <div className="mb-6 space-y-2" data-testid="pending-asks">
      {asks.map(ask => {
        const first = (ask.requester_name || "Someone").split(" ")[0];
        return (
          <div
            key={ask.request_id}
            className="flex items-center gap-3 p-3 rounded-xl"
            style={{
              background: "var(--color-brand-ghost)",
              border: "1px solid var(--color-border-active)",
            }}
            data-testid={`ask-${ask.request_id}`}
          >
            <div className="relative flex-shrink-0">
              {ask.requester_picture ? (
                <img
                  src={ask.requester_picture}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover"
                  style={{ border: "2px solid var(--color-surface)" }}
                />
              ) : (
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: "var(--color-brand-primary)", color: "var(--color-text-on-brand)", fontWeight: 600 }}
                >
                  {first.charAt(0).toUpperCase()}
                </div>
              )}
              <span
                className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center"
                style={{ background: "var(--color-brand-primary)", border: "2px solid var(--color-brand-ghost)" }}
              >
                <MessageSquareHeart size={8} color="#fff" />
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                {first} is asking what you&apos;re grateful for in them.
              </p>
              {ask.prompt && (
                <p className="text-xs italic truncate mt-0.5" style={{ color: "var(--color-text-secondary)" }}>
                  &ldquo;{ask.prompt}&rdquo;
                </p>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button
                size="sm"
                onClick={() => onRespond(ask)}
                className="rounded-full h-8 text-xs"
                style={{ background: "var(--color-brand-primary)", color: "var(--color-text-on-brand)" }}
                data-testid={`respond-${ask.request_id}`}
              >
                <Check size={12} className="mr-1" />
                Respond
              </Button>
              <button
                onClick={() => onDecline(ask)}
                className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-black/5"
                style={{ color: "var(--color-text-secondary)" }}
                data-testid={`decline-${ask.request_id}`}
                aria-label="Decline ask"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// --- Empty state (NO connections, NO pending seeds) -----------------------

const EmptyState = ({ onStartOne, onSeedThree, onWriteToSelf, onSendFirst }) => {
  const [showMore, setShowMore] = useState(false);
  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: "linear-gradient(180deg, var(--color-brand-ghost) 0%, var(--color-surface) 80%)",
        border: "1px solid var(--color-border-default)",
      }}
      data-testid="wall-empty"
    >
      <div className="flex flex-col items-center text-center px-6 py-12">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
          style={{ background: "var(--color-brand-primary)", color: "var(--color-text-on-brand)" }}
        >
          <Sparkles size={22} />
        </div>
        <h2 className="text-2xl font-bold mb-2" style={{ color: "var(--color-text-primary)", letterSpacing: "-0.01em" }}>
          The people who love you haven&apos;t said it yet.
        </h2>
        <p className="text-sm max-w-lg mb-6" style={{ color: "var(--color-text-secondary)", lineHeight: 1.65 }}>
          Gratitude is usually private. Here, it can travel both ways — ask someone what
          they appreciate in you, and write what you appreciate in them. Even if they&apos;re
          not on Evoluras yet.
        </p>
        <Button
          onClick={onStartOne}
          className="rounded-full px-7 h-11"
          style={{
            background: "var(--color-brand-primary)",
            color: "var(--color-text-on-brand)",
            boxShadow: "0 4px 14px -4px rgba(83, 74, 183, 0.4)",
          }}
          data-testid="empty-start-one"
        >
          <Mail size={15} className="mr-2" />
          Start with one
        </Button>
        <p className="text-xs mt-3" style={{ color: "var(--color-text-placeholder)" }}>
          Write to someone by email — they don&apos;t need an account
        </p>

        <button
          onClick={() => setShowMore(!showMore)}
          className="mt-6 text-xs font-medium inline-flex items-center gap-1 hover:underline"
          style={{ color: "var(--color-text-secondary)" }}
          data-testid="empty-more-toggle"
        >
          <ChevronDown
            size={12}
            style={{ transform: showMore ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
          />
          More ways
        </button>

        {showMore && (
          <div className="mt-4 w-full max-w-md space-y-2" data-testid="empty-more-ways">
            <EmptyOption
              icon={<Target size={16} />}
              title="Seed 3 people at once"
              sub="Send the same note to 3 people who know you well"
              onClick={onSeedThree}
              testId="empty-seed-three"
            />
            <EmptyOption
              icon={<Gift size={16} />}
              title="Send gratitude to someone else first"
              sub="Many people write back when they receive one"
              onClick={onSendFirst}
              testId="empty-send-first"
            />
            <EmptyOption
              icon={<Plus size={16} />}
              title="Write one to yourself"
              sub="A small thing that made today better"
              onClick={onWriteToSelf}
              testId="empty-write-self"
            />
          </div>
        )}
      </div>
    </div>
  );
};

const EmptyOption = ({ icon, title, sub, onClick, testId }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all hover:-translate-y-0.5"
    style={{
      background: "var(--color-surface)",
      border: "1px solid var(--color-border-default)",
    }}
    data-testid={testId}
  >
    <div
      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
      style={{ background: "var(--color-brand-ghost)", color: "var(--color-brand-primary)" }}
    >
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>{title}</div>
      <div className="text-xs" style={{ color: "var(--color-text-secondary)" }}>{sub}</div>
    </div>
    <ArrowRight size={14} style={{ color: "var(--color-text-placeholder)" }} />
  </button>
);

// --- Wait state (0 received, but user has sent pending seeds) --------------

const WaitState = ({ pendingOutgoing, onAddSelf, onSeedThree, onSwot, navigate }) => {
  const asks = pendingOutgoing?.asks || [];
  const cardCount = pendingOutgoing?.offered_invites || 0;
  const total = pendingOutgoing?.total || 0;

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-border-default)",
      }}
      data-testid="wall-wait"
    >
      <div className="px-6 py-8">
        <div className="flex items-start gap-3 mb-4">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: "var(--color-brand-ghost)", color: "var(--color-brand-primary)" }}
          >
            <Clock size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold" style={{ color: "var(--color-text-primary)" }}>
              Your notes are out there.
            </h2>
            <p className="text-sm mt-0.5" style={{ color: "var(--color-text-secondary)", lineHeight: 1.55 }}>
              This kind of message isn&apos;t answered in 30 seconds. Give it a day or two.
            </p>
          </div>
        </div>

        {asks.length > 0 && (
          <div className="mb-4 space-y-1.5" data-testid="wait-pending-list">
            <div
              className="text-[10px] font-semibold uppercase mb-1"
              style={{ color: "var(--color-text-label)", letterSpacing: "0.1em" }}
            >
              Waiting to hear from
            </div>
            {asks.slice(0, 5).map(a => (
              <div
                key={a.request_id}
                className="flex items-center gap-2 text-sm"
                style={{ color: "var(--color-text-primary)" }}
              >
                <UserPlus size={12} style={{ color: "var(--color-text-placeholder)" }} />
                <span className="font-medium">{a.pending_name}</span>
                <span style={{ color: "var(--color-text-placeholder)" }}>·</span>
                <span className="truncate" style={{ color: "var(--color-text-secondary)" }}>{a.pending_email}</span>
              </div>
            ))}
            {asks.length > 5 && (
              <div className="text-xs" style={{ color: "var(--color-text-placeholder)" }}>
                + {asks.length - 5} more
              </div>
            )}
          </div>
        )}
        {cardCount > 0 && asks.length === 0 && (
          <p className="text-sm mb-4" style={{ color: "var(--color-text-secondary)" }}>
            You&apos;ve offered gratitude to {cardCount} {cardCount === 1 ? "person" : "people"} who aren&apos;t on Evoluras yet.
          </p>
        )}

        <div
          className="pt-4 mt-4"
          style={{ borderTop: "0.5px solid var(--color-border-default)" }}
        >
          <div
            className="text-[10px] font-semibold uppercase mb-3"
            style={{ color: "var(--color-text-label)", letterSpacing: "0.1em" }}
          >
            While you wait
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={onAddSelf}
              variant="outline"
              size="sm"
              className="rounded-full text-xs"
              style={{ borderColor: "var(--color-border-default)", color: "var(--color-text-primary)" }}
              data-testid="wait-add-self"
            >
              <Plus size={12} className="mr-1" /> Write something for yourself
            </Button>
            {total < 5 && (
              <Button
                onClick={onSeedThree}
                variant="outline"
                size="sm"
                className="rounded-full text-xs"
                style={{ borderColor: "var(--color-border-default)", color: "var(--color-text-primary)" }}
                data-testid="wait-seed-more"
              >
                <Target size={12} className="mr-1" /> Ask a few more people
              </Button>
            )}
            <Button
              onClick={() => navigate("/tools/swot")}
              variant="outline"
              size="sm"
              className="rounded-full text-xs"
              style={{ borderColor: "var(--color-border-default)", color: "var(--color-text-primary)" }}
              data-testid="wait-swot"
            >
              <Sparkles size={12} className="mr-1" /> Try your Personal SWOT
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Main Page --------------------------------------------------------------

const GratitudeWallPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [cards, setCards] = useState([]);
  const [matches, setMatches] = useState([]);
  const [pendingAsks, setPendingAsks] = useState([]);
  const [wallVisibility, setWallVisibility] = useState("public");
  const [slug, setSlug] = useState(null);
  const [username, setUsername] = useState(null);
  const [joinedAt, setJoinedAt] = useState(null);
  const [joinedPicture, setJoinedPicture] = useState(null);
  const [connections, setConnections] = useState([]);
  const [wheelAreaMap, setWheelAreaMap] = useState({});
  const [pendingOutgoing, setPendingOutgoing] = useState({ asks: [], offered_invites: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("both");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("self");
  const [dialogConnectionId, setDialogConnectionId] = useState(null);
  const [dialogRequestId, setDialogRequestId] = useState(null);
  const [dialogRequester, setDialogRequester] = useState(null);
  const [seedDialogOpen, setSeedDialogOpen] = useState(false);
  // Card currently being edited via <EditGratitudeDialog/>. null = closed.
  const [editingCard, setEditingCard] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [wallRes, netRes, wheelRes, matchesRes] = await Promise.all([
        fetch(`${API}/gratitude/wall`, { credentials: "include" }),
        fetch(`${API}/network`, { credentials: "include" }),
        fetch(`${API}/tools/wheel`, { credentials: "include" }),
        fetch(`${API}/gratitude/matches`, { credentials: "include" }),
      ]);
      if (wallRes.ok) {
        const data = await wallRes.json();
        setCards(data.cards || []);
        setPendingAsks(data.pending_asks || []);
        setWallVisibility(data.wall_visibility || "public");
        setSlug(data.slug || null);
        setUsername(data.username || null);
        setJoinedAt(data.joined_at || null);
        setJoinedPicture(data.picture || null);
        setPendingOutgoing(data.pending_outgoing || { asks: [], offered_invites: 0, total: 0 });
      }
      if (netRes.ok) setConnections(await netRes.json());
      if (matchesRes.ok) {
        const m = await matchesRes.json();
        setMatches(m.matches || []);
      }
      if (wheelRes.ok) {
        const data = await wheelRes.json();
        const active = (data.wheel?.areas && data.wheel.areas.length > 0)
          ? data.wheel.areas
          : (data.default_areas || []);
        const map = {};
        active.forEach(a => { map[a.key] = a; });
        setWheelAreaMap(map);
      }
    } catch {
      toast.error("Failed to load wall");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Deep link from email: /tools/gratitude?respond={request_id}
  useEffect(() => {
    const respondId = searchParams.get("respond");
    if (respondId && pendingAsks.length > 0) {
      const ask = pendingAsks.find(a => a.request_id === respondId);
      if (ask) openRespondDialog(ask);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, pendingAsks]);

  // Deep link from bell: /tools/gratitude?tab=received
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "received" || tab === "mine" || tab === "given" || tab === "both") {
      setFilter(tab);
    }
  }, [searchParams]);

  const openAddSelf = () => {
    setDialogMode("self"); setDialogConnectionId(null); setDialogRequestId(null); setDialogRequester(null); setDialogOpen(true);
  };
  const openOffer = () => {
    setDialogMode("offered"); setDialogConnectionId(null); setDialogRequestId(null); setDialogRequester(null); setDialogOpen(true);
  };
  const openAsk = () => {
    setDialogMode("ask"); setDialogConnectionId(null); setDialogRequestId(null); setDialogRequester(null); setDialogOpen(true);
  };
  const openRespondDialog = (ask) => {
    const conn = connections.find(c =>
      (c.user_id === ask.requester_id || c.connected_user_id === ask.requester_id) && c.status === "accepted"
    );
    if (!conn) {
      toast.error("Can't find your connection with this person");
      return;
    }
    setDialogMode("offered");
    setDialogConnectionId(conn.connection_id);
    setDialogRequestId(ask.request_id);
    setDialogRequester(ask.requester_name);
    setDialogOpen(true);
  };

  const declineAsk = async (ask) => {
    try {
      const res = await fetch(`${API}/gratitude/requests/${ask.request_id}/decline`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("decline failed");
      toast.success("Ask declined");
      fetchAll();
    } catch {
      toast.error("Failed to decline");
    }
  };

  const deleteCard = async (card) => {
    if (!confirm("Delete this card? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API}/gratitude/cards/${card.card_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("delete failed");
      toast.success("Card removed");
      fetchAll();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const setTargetVisibility = async (card, value, { toastSuccess } = {}) => {
    try {
      const res = await fetch(`${API}/gratitude/cards/${card.card_id}/target-visibility`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value }),
      });
      if (!res.ok) throw new Error("update failed");
      const body = await res.json().catch(() => ({}));
      if (body?.matched) {
        // Celebratory toast — fires only on the 0→matched transition thanks
        // to backend-side idempotency in recompute_and_maybe_notify.
        toast.success("Gratitude came full circle ✨", {
          description: "You're on each other's walls now.",
          duration: 5500,
        });
      } else if (toastSuccess) {
        toast.success(toastSuccess);
      }
      fetchAll();
    } catch {
      toast.error("Failed to update");
    }
  };

  const approveCard = (card, value) =>
    setTargetVisibility(card, value, {
      toastSuccess: value === "public" ? "Added to your wall" : "Kept just for you",
    });

  const hideCard = (card) =>
    setTargetVisibility(card, "removed", { toastSuccess: "Removed — you can restore it anytime" });

  const recoverCard = (card) =>
    setTargetVisibility(card, "public", { toastSuccess: "Restored to your wall" });

  const cycleVisibility = async (card, nextExplicit = null) => {
    // Popover picker passes the chosen value explicitly. Legacy callers
    // (keyboard shortcuts, etc.) that call without a second arg fall back
    // to the old 2-way toggle between private and public.
    const next = nextExplicit || (card.visibility === "public" ? "private" : "public");
    if (next === card.visibility) return;
    try {
      const res = await fetch(`${API}/gratitude/cards/${card.card_id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visibility: next }),
      });
      if (!res.ok) throw new Error("visibility failed");
      toast.success(
        next === "public"
          ? "Everyone can see this now"
          : next === "circle"
          ? "Shared with your inner circle"
          : "Kept to yourself"
      );
      fetchAll();
    } catch {
      toast.error("Failed to change privacy");
    }
  };

  const editCard = (card) => {
    // Opens <EditGratitudeDialog/> — see the render tree below. Actual PATCH
    // call lives inside the dialog so we can show proper validation +
    // multi-image management UI instead of the old native prompt().
    setEditingCard(card);
  };

  const toggleWallVisibility = async () => {
    const next = wallVisibility === "public" ? "private" : "public";
    try {
      const res = await fetch(`${API}/gratitude/wall-visibility`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visibility: next }),
      });
      if (!res.ok) throw new Error("toggle failed");
      setWallVisibility(next);
      toast.success(next === "public" ? "Wall is public" : "Wall is private");
    } catch {
      toast.error("Failed to update");
    }
  };

  const copyPublicLink = () => {
    if (!slug && !username) return;
    // Share the backend shim so link-preview apps (WhatsApp, Slack, Twitter,
    // LinkedIn, Telegram, Discord, etc.) fetch a personalized HTML page with
    // a dynamic branded og:image + per-user og:title/og:description. The
    // shim emits <meta http-equiv="refresh"> + a JS redirect that lands real
    // browsers on the clean `/:username/gratitude` URL in ~0ms.
    //
    // Only explicit share actions use the shim — the canonical UI URL for
    // the wall stays `/:username/gratitude` everywhere else (sidebar, chips,
    // emails, browser bar after landing).
    const identifier = username || slug;
    const url = `${window.location.origin}/api/share/${encodeURIComponent(identifier)}/wall`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  const filtered = cards.filter(c => {
    const isRemoved = c.perspective === "received" && c.target_visibility === "removed";
    if (filter === "removed") return isRemoved;
    if (isRemoved) return false;  // hide removed from all other views
    if (filter === "both") return c.perspective === "mine" || c.perspective === "received";
    return c.perspective === filter;
  });

  const counts = {
    both: cards.filter(c =>
      (c.perspective === "mine" || c.perspective === "received") &&
      !(c.perspective === "received" && c.target_visibility === "removed")
    ).length,
    mine: cards.filter(c => c.perspective === "mine").length,
    given: cards.filter(c => c.perspective === "given").length,
    received: cards.filter(c => c.perspective === "received" && c.target_visibility !== "removed").length,
    removed: cards.filter(c => c.perspective === "received" && c.target_visibility === "removed").length,
    pending: cards.filter(c => c.perspective === "received" && c.target_visibility === "pending").length,
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div
          className="w-8 h-8 border-2 rounded-full animate-spin"
          style={{ borderColor: "var(--color-brand-primary)", borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  return (
    <>
      <SEO
        title={t("gratitude.title")}
        description={t("gratitude.subtitle")}
        path="/tools/gratitude"
      />

      <div data-testid="gratitude-wall-page">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          {/* Page Header (matches MyselfPage pattern) */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: "var(--color-text-primary)" }}>
                {t("gratitude.title")}
              </h1>
              <p className="text-sm mt-0.5" style={{ color: "var(--color-text-secondary)" }}>
                {t("gratitude.subtitle")}
              </p>
            </div>

            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                onClick={toggleWallVisibility}
                className="inline-flex items-center justify-center rounded-full w-9 h-9 transition-colors"
                style={{
                  background: wallVisibility === "public" ? "var(--color-brand-ghost)" : "var(--color-surface)",
                  color: wallVisibility === "public" ? "var(--color-brand-primary)" : "var(--color-text-secondary)",
                  border: `1px solid ${wallVisibility === "public" ? "var(--color-brand-primary)" : "var(--color-border-default)"}`,
                }}
                aria-label={wallVisibility === "public" ? "Wall is public — click to make private" : "Wall is private — click to make public"}
                title={wallVisibility === "public" ? "Public wall" : "Private wall"}
                data-testid="wall-visibility-toggle"
              >
                {wallVisibility === "public" ? <Globe size={14} /> : <Lock size={14} />}
              </button>
              {wallVisibility === "public" && (slug || username) && (
                <button
                  onClick={copyPublicLink}
                  className="inline-flex items-center justify-center rounded-full w-9 h-9 transition-colors hover:bg-black/5"
                  style={{
                    background: "var(--color-surface)",
                    color: "var(--color-text-secondary)",
                    border: "1px solid var(--color-border-default)",
                  }}
                  aria-label="Copy public wall link"
                  title="Copy public link"
                  data-testid="wall-share-link"
                >
                  <Share2 size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Action row */}
          <div className="flex flex-wrap gap-2 mb-6" data-testid="gratitude-actions">
            <Button
              onClick={openAddSelf}
              className="rounded-full"
              style={{ background: "var(--color-brand-primary)", color: "var(--color-text-on-brand)" }}
              data-testid="action-self"
            >
              <Plus size={14} className="mr-1.5" /> {t("gratitude.addSelf")}
            </Button>
            <Button
              onClick={openOffer}
              variant="outline"
              className="rounded-full"
              style={{ borderColor: "var(--color-border-default)", color: "var(--color-text-primary)" }}
              data-testid="action-offer"
            >
              <Gift size={14} className="mr-1.5" /> {t("gratitude.sendYours")}
            </Button>
            <Button
              onClick={openAsk}
              variant="outline"
              className="rounded-full"
              style={{ borderColor: "var(--color-border-default)", color: "var(--color-text-primary)" }}
              data-testid="action-ask"
            >
              <MessageSquareHeart size={14} className="mr-1.5" /> {t("gratitude.askSomeone")}
            </Button>
          </div>

          {/* Pending asks banner */}
          <PendingAsksBanner
            asks={pendingAsks}
            onRespond={openRespondDialog}
            onDecline={declineAsk}
          />

          {/* Matched pairs (mutual gratitude) — above the main feed */}
          <MatchedPairs matches={matches} wheelAreaMap={wheelAreaMap} />

          {/* Gratitude rhythm — weekly/monthly timeline */}
          <GratitudeTimeline
            cards={cards}
            wheelAreaMap={wheelAreaMap}
            joinedAt={joinedAt}
            joinedPicture={joinedPicture}
          />

          {/* Filter chips */}
          {cards.length > 0 && (
            <div className="flex items-center justify-between gap-3 mb-6 flex-wrap" data-testid="wall-filters">
              <div className="flex gap-2 flex-wrap">
                {FILTERS.map(f => {
                  const active = filter === f.key;
                  return (
                    <button
                      key={f.key}
                      onClick={() => setFilter(f.key)}
                      className="text-xs font-medium px-3 py-1.5 rounded-full transition-colors"
                      style={{
                        background: active ? "var(--color-brand-primary)" : "transparent",
                        color: active ? "var(--color-text-on-brand)" : "var(--color-text-secondary)",
                        border: `1px solid ${active ? "var(--color-brand-primary)" : "var(--color-border-default)"}`,
                      }}
                      data-testid={`filter-${f.key}`}
                    >
                      {t(f.label_key)}
                      <span className="ml-1.5 opacity-70" style={{ fontWeight: 400 }}>
                        {counts[f.key]}
                      </span>
                    </button>
                  );
                })}
              </div>
              {counts.given > 0 && (
                <button
                  onClick={() => setFilter(filter === "given" ? "both" : "given")}
                  className="text-xs font-medium inline-flex items-center gap-1 hover:underline"
                  style={{
                    color: filter === "given" ? "var(--color-brand-primary)" : "var(--color-text-secondary)",
                  }}
                  data-testid="filter-given"
                >
                  {filter === "given" ? t("gratitude.backFilter") : `${t("gratitude.given", { count: counts.given })} →`}
                </button>
              )}
              {counts.removed > 0 && (
                <button
                  onClick={() => setFilter(filter === "removed" ? "both" : "removed")}
                  className="text-xs font-medium inline-flex items-center gap-1 hover:underline"
                  style={{
                    color: filter === "removed" ? "var(--color-brand-primary)" : "var(--color-text-secondary)",
                  }}
                  data-testid="filter-removed"
                >
                  {filter === "removed" ? t("gratitude.backFilter") : `${t("gratitude.removed", { count: counts.removed })}`}
                </button>
              )}
            </div>
          )}

          {/* Wall */}
          {cards.length === 0 ? (
            pendingOutgoing.total > 0 ? (
              <WaitState
                pendingOutgoing={pendingOutgoing}
                onAddSelf={openAddSelf}
                onSeedThree={() => setSeedDialogOpen(true)}
                onSwot={() => navigate("/tools/swot")}
                navigate={navigate}
              />
            ) : (
              <EmptyState
                onStartOne={openOffer}
                onSeedThree={() => setSeedDialogOpen(true)}
                onWriteToSelf={openAddSelf}
                onSendFirst={openOffer}
              />
            )
          ) : (
            <div
              style={{ columnGap: 16, columnCount: 1 }}
              className="gratitude-masonry"
              data-testid="wall-grid"
            >
              {filtered.map(card => (
                <div
                  key={card.card_id}
                  style={{ breakInside: "avoid", marginBottom: 16, display: "block" }}
                >
                  <GratitudeCard
                    card={card}
                    onEdit={editCard}
                    onDelete={deleteCard}
                    onHide={hideCard}
                    onVisibility={cycleVisibility}
                    onApprove={approveCard}
                    onRecover={recoverCard}
                    wheelAreaMap={wheelAreaMap}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @media (min-width: 640px) { .gratitude-masonry { column-count: 2 !important; } }
        @media (min-width: 1024px) { .gratitude-masonry { column-count: 3 !important; } }
      `}</style>

      <AddGratitudeDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        mode={dialogMode}
        onCreated={fetchAll}
        connections={connections}
        initialConnectionId={dialogConnectionId}
        initialRequestId={dialogRequestId}
        initialRequester={dialogRequester}
      />

      <SeedGratitudeDialog
        open={seedDialogOpen}
        onClose={() => setSeedDialogOpen(false)}
        onSent={fetchAll}
      />

      <EditGratitudeDialog
        open={!!editingCard}
        card={editingCard}
        onClose={() => setEditingCard(null)}
        onSaved={() => { setEditingCard(null); fetchAll(); }}
      />
    </>
  );
};

export default GratitudeWallPage;
